const CONFIG = {
  cookieName: 'cart_token_expiry',
  refreshTimings: [5, 3, 1], // Minutes before expiry
  storageKey: 'cart_refresh_lock', // localStorage key
  redirectedFlag: 'cart_refresh_redirected',
  lockDuration: 2000, // 2 sec of block for race conditions from other tabs

  loginUrl: '/login/',
  addReturnUrl: true,
  shouldRedirectToLogin: true
};

class CartTokenRefresher {
  constructor() {
    this.timeouts = [];
    this.lastExpiryTime = null;
    this.isRefreshing = false;
    this.cookieExistedInitially = false;
    this.hasRedirected = false;
    this.region = null; // Taken from window.Cms.CurrentLanguageRegion

    this.onStorage = (e) => this.handleStorageChange(e);
    this.onVisibility = () => this.handleVisibilityChange();

    window.addEventListener('storage', this.onStorage);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.initialize();
  }

  initialize() {
    console.log('[TokenRefresh] Initializing...');

    this.region = this.getRegion();
    console.debug('[TokenRefresh] Detected region:', this.region || '(none)');

    this.clearAllTimeouts();

    const expiryTime = this.getExpiryTimeFromCookie();

    if (!expiryTime) {
      console.log('[TokenRefresh] No expiry cookie found');
      this.cookieExistedInitially = false;
      return;
    }

    this.clearRedirectFlag();

    this.cookieExistedInitially = true;
    this.lastExpiryTime = expiryTime;
    this.scheduleRefreshes(expiryTime);
  }

  getRegion() {
    try {
      if (window.Cms && window.Cms.CurrentLanguageRegion) {
        const region = window.Cms.CurrentLanguageRegion;
        console.debug('[TokenRefresh] Region from window.Cms.CurrentLanguageRegion:', region);
        return region;
      }

      console.warn('[TokenRefresh] window.Cms.CurrentLanguageRegion not found');
      return null;
    } catch (error) {
      console.error('[TokenRefresh] Error getting region:', error);
      return null;
    }
  }

  getRefreshEndpoint() {
    if (!this.region) {
      console.error('[TokenRefresh] Cannot build endpoint URL - region is null');
      return null;
    }

    return `/${this.region}/login/refreshToken?regionName=${this.region}`;
  }

  getLogoutEndpoint() {
    if (!this.region) {
      console.error('[Logout] Cannot build endpoint URL - region is null');
      return null;
    }

    return `/${this.region}/login/logout`;
  }

  getExpiryTimeFromCookie() {
    const cookies = document.cookie.split(';');
    const cartCookie = cookies.find((c) => c.trim().startsWith(CONFIG.cookieName + '='));

    if (!cartCookie) return null;

    try {
      const [name, ...valueParts] = cartCookie.split('=');
      const encodedValue = valueParts.join('=');
      const decodedValue = decodeURIComponent(encodedValue);

      console.debug('[TokenRefresh] Raw cookie value:', decodedValue);

      let dateString = decodedValue.trim();

      const expiryDate = new Date(dateString);

      if (isNaN(expiryDate.getTime())) {
        console.error('[TokenRefresh] Invalid date format:', decodedValue);
        return null;
      }

      console.debug(
        '[TokenRefresh] Parsed expiry time:',
        expiryDate.toISOString(),
        '(Local:',
        expiryDate.toLocaleString(),
        ')'
      );

      return expiryDate;
    } catch (error) {
      console.error('[TokenRefresh] Error parsing cookie:', error);
      return null;
    }
  }

  scheduleRefreshes(expiryTime) {
    const now = new Date();
    const msUntilExpiry = expiryTime - now;

    console.debug('[TokenRefresh] Time until expiry:', Math.floor(msUntilExpiry / 1000), 'seconds');

    if (msUntilExpiry <= 0) {
      console.debug('[TokenRefresh] Token already expired');
      this.handleTokenExpired();
      return;
    }

    CONFIG.refreshTimings.forEach((minutesBefore) => {
      const msBeforeExpiry = minutesBefore * 60 * 1000;
      const triggerTime = msUntilExpiry - msBeforeExpiry;

      if (triggerTime <= 0) {
        console.debug(`[TokenRefresh] Skipping ${minutesBefore}min refresh (time passed)`);
        return;
      }

      console.debug(
        `[TokenRefresh] Scheduling refresh for ${minutesBefore}min before expiry (in ${Math.floor(triggerTime / 1000)}s)`
      );

      const timeoutId = setTimeout(() => {
        this.performRefresh(minutesBefore);
      }, triggerTime);

      this.timeouts.push(timeoutId);
    });

    console.debug(
      `[TokenRefresh] Scheduling expiry handler (in ${Math.floor(msUntilExpiry / 1000)}s)`
    );
    const expiryTimeoutId = setTimeout(() => {
      this.handleTokenExpired();
    }, msUntilExpiry);

    this.timeouts.push(expiryTimeoutId);
  }

  async performRefresh(minutesBefore) {
    if (this.isRefreshing) {
      console.debug('[TokenRefresh] Already refreshing, skipping');
      return;
    }
    const endpoint = this.getRefreshEndpoint();
    if (!endpoint) {
      console.error(`[TokenRefresh] Cannot perform ${minutesBefore}min refresh - no endpoint`);
      return;
    }

    if (this.isAnotherTabRefreshing()) {
      console.debug(
        `[TokenRefresh] Another tab is refreshing, skipping ${minutesBefore}min refresh`
      );
      return;
    }

    this.setRefreshLock();
    this.isRefreshing = true;

    try {
      console.log(`[TokenRefresh] Performing ${minutesBefore}min refresh to: ${endpoint}`);

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log(`[TokenRefresh] ${minutesBefore}min refresh successful`);

        setTimeout(() => {
          this.handleSuccessfulRefresh();
        }, 1000);
      } else {
        console.error(`[TokenRefresh] ${minutesBefore}min refresh failed:`, response.status);
      }
    } catch (error) {
      console.error(`[TokenRefresh] ${minutesBefore}min refresh error:`, error);
    } finally {
      this.isRefreshing = false;
      this.clearRefreshLock();
    }
  }

  async performLogout() {
    const endpoint = this.getLogoutEndpoint();
    if (!endpoint) {
      console.error(`[Logout] Cannot logout - no endpoint`);
      return;
    }

    try {
      console.log(`[Logout] Performing logout: ${endpoint}`);

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log(`[Logout] successful`);
      } else {
        console.error(`[Logout] failed:`, response.status);
      }
    } catch (error) {
      console.error(`[Logout] error:`, error);
    }
  }

  handleSuccessfulRefresh() {
    const newExpiryTime = this.getExpiryTimeFromCookie();

    if (!newExpiryTime) {
      console.debug('[TokenRefresh] No cookie after refresh');
      return;
    }

    if (this.lastExpiryTime && newExpiryTime.getTime() === this.lastExpiryTime.getTime()) {
      console.debug('[TokenRefresh] Expiry time unchanged');
      return;
    }

    console.log('[TokenRefresh] Cookie updated, rescheduling refreshes');
    this.lastExpiryTime = newExpiryTime;

    this.clearAllTimeouts();
    this.scheduleRefreshes(newExpiryTime);
  }

  isAnotherTabRefreshing() {
    const raw = localStorage.getItem(CONFIG.storageKey);
    if (!raw) return false;

    try {
      const { expiresAt } = JSON.parse(raw);
      return Date.now() < expiresAt;
    } catch {
      return false;
    }
  }

  setRefreshLock() {
    localStorage.setItem(CONFIG.storageKey, JSON.stringify({ expiresAt: Date.now() + 10000 }));
  }

  clearRefreshLock() {
    try {
      localStorage.removeItem(CONFIG.storageKey);
    } catch (e) {
      console.warn('[TokenRefresh] Could not clear lock:', e);
    }
  }

  handleStorageChange(event) {
    if (event.key === CONFIG.storageKey && event.newValue) {
      console.debug('[TokenRefresh] Another tab is performing refresh');
    }
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      console.debug('[TokenRefresh] Tab became visible, checking state...');

      const currentExpiryTime = this.getExpiryTimeFromCookie();

      if (!currentExpiryTime) {
        console.debug('[TokenRefresh] Cookie disappeared while tab was hidden');
        this.clearAllTimeouts();
        return;
      }

      if (!this.lastExpiryTime || currentExpiryTime.getTime() !== this.lastExpiryTime.getTime()) {
        console.debug('[TokenRefresh] Cookie changed while tab was hidden, reinitializing');
        this.initialize();
      }
    }
  }

  clearAllTimeouts() {
    this.timeouts.forEach((id) => clearTimeout(id));
    this.timeouts = [];
    console.debug('[TokenRefresh] All timeouts cleared');
  }

  async manualRefresh() {
    console.log('[TokenRefresh] Manual refresh triggered');
    await this.performRefresh('manual');
  }

  destroy() {
    this.clearAllTimeouts();
    this.clearRefreshLock();
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('visibilitychange', this.onVisibility);
    console.debug('[TokenRefresh] Destroyed');
  }

  handleTokenExpired() {
    console.log('[TokenRefresh] Token expired!');

    if (!this.cookieExistedInitially) {
      console.debug('[TokenRefresh] Cookie never existed, skipping redirect');
      return;
    }

    if (this.hasRedirected) {
      console.debug('[TokenRefresh] Already redirected, skipping');
      return;
    }

    if (this.wasRecentlyRedirected()) {
      console.debug('[TokenRefresh] Recently redirected (from sessionStorage), skipping');
      return;
    }

    if (this.isOnLoginPage()) {
      this.performLogout();
      console.debug('[TokenRefresh] Already on login page, skipping redirect');
      return;
    }

    if (CONFIG.shouldRedirectToLogin) {
      console.log('[TokenRefresh] Redirecting to login...');
      this.hasRedirected = true;
      this.markAsRedirected();
      this.clearAllTimeouts();
      this.performLogout();
      this.redirectToLogin();
    }
  }

  isOnLoginPage() {
    const currentPath = window.location.pathname.toLowerCase();
    const loginPath = CONFIG.loginUrl.toLowerCase();

    return currentPath.includes(loginPath) || currentPath.includes('/login');
  }

  wasRecentlyRedirected() {
    try {
      return sessionStorage.getItem(CONFIG.redirectedFlag) === 'true';
    } catch (e) {
      return false;
    }
  }

  markAsRedirected() {
    try {
      sessionStorage.setItem(CONFIG.redirectedFlag, 'true');
    } catch (e) {
      console.warn('[TokenRefresh] Could not set redirect flag:', e);
    }
  }

  clearRedirectFlag() {
    try {
      sessionStorage.removeItem(CONFIG.redirectedFlag);
    } catch (e) {
      console.warn('[TokenRefresh] Could not clear redirect flag:', e);
    }
  }

  redirectToLogin() {
    const regionPrefix = this.region ? `/${this.region}` : '';
    let loginUrl = `${regionPrefix}${CONFIG.loginUrl}`;

    if (CONFIG.addReturnUrl) {
      const currentPath = window.location.pathname + window.location.search;
      const returnUrl = encodeURIComponent(currentPath);

      const separator = loginUrl.includes('?') ? '&' : '?';
      loginUrl = `${loginUrl}${separator}returnUrl=${returnUrl}`;
    }

    console.log(`[TokenRefresh] Redirecting to: ${loginUrl}`);
    document.cookie = `${CONFIG.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    window.location.href = loginUrl;
  }
}

export function initCartTokenRefresher() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.cartRefresher = new CartTokenRefresher();
    });
  } else {
    window.cartRefresher = new CartTokenRefresher();
  }

  return window.cartRefresher;
}

export { CartTokenRefresher };
